import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from '../../../../Common/PageHeader';
import ContentCard from '../../../../Common/Card';
import MenuIcon from '../../../../Layout/Menu/MenuIcon';
import { accountData } from '../../../../Layout/Account/Data';
import Accounts from '../Accounts';
import ViewerForm, { ViewerFormData } from '../Form';
import Icons from '../../../../../../components/Common/Icons/Icons';
import { cn } from '../../../../../../utils/styles';
import { Button } from '../../../../../../components/Common/UI/Button';
import { Input } from '../../../../../../components/Common/UI/Input';
import FakePagination from '../../../../../../components/Common/UI/FakePagination';
import { notifyError } from '../../../../../../components/Common/UI/Notify';

export interface AuthorizedViewer {
  id: string;
  name: string;
  email: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  securityQuestion?: string;
  securityAnswer?: string;
  selectedAccounts?: string[];
}

function CreateAuthorizedViewers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<ViewerFormData>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    securityQuestion: '',
    securityAnswer: '',
  });

  const viewer = location.state?.viewer as AuthorizedViewer | undefined;
  const isEditing = !!viewer;
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

  useEffect(() => {
    setSelectedAccounts(viewer?.selectedAccounts || []);
  }, [viewer?.id]);

  useEffect(() => {
    if (viewer) {
      setFormData({
        firstName: viewer.firstName || '',
        lastName: viewer.lastName || '',
        username: viewer.username || '',
        email: viewer.email || '',
        password: '',
        confirmPassword: '',
        securityQuestion: viewer.securityQuestion || '',
        securityAnswer: viewer.securityAnswer || '',
      });
    }
  }, [viewer?.id]);

  const handleAccountSelect = (accountNumber: string, checked: boolean) => {
    setSelectedAccounts(prev => {
      if (checked) {
        return [...prev, accountNumber];
      }

      return prev.filter(num => num !== accountNumber);
    });
  };

  const handleSave = () => {
    if (selectedAccounts.length === 0) {
      notifyError('Please select at least one account', 2000, { backgroundColor: '#DBE8E2' });

      return;
    }

    if (isEditing && viewer) {
      const updatedViewer: AuthorizedViewer = {
        ...viewer,
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        email: formData.email,
        securityQuestion: formData.securityQuestion,
        securityAnswer: formData.securityAnswer,
        name: formData.username || viewer.name,
        selectedAccounts,
      };

      const updates = JSON.parse(sessionStorage.getItem('viewerUpdates') || '[]');
      const filteredUpdates = updates.filter((u: AuthorizedViewer) => u.id !== viewer.id);

      sessionStorage.setItem('viewerUpdates', JSON.stringify([...filteredUpdates, updatedViewer]));
      localStorage.setItem('viewerUpdates', JSON.stringify([...filteredUpdates, updatedViewer]));
    } else {
      const newViewer: AuthorizedViewer = {
        id: Date.now().toString(),
        name: formData.username || 'New Viewer',
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        securityQuestion: formData.securityQuestion,
        securityAnswer: formData.securityAnswer,
        selectedAccounts,
      };

      const existing = JSON.parse(sessionStorage.getItem('tempViewers') || '[]');

      sessionStorage.setItem('tempViewers', JSON.stringify([...existing, newViewer]));
    }

    navigate('/guidance/authorized-viewers');
  };

  const checkingData = accountData.find(el => el.type === 'checking');
  const loanData = accountData.find(el => el.type === 'loan');

  const filteredData = [
    {
      ...checkingData,
      accounts: checkingData?.accounts.slice(0, 1) || [],
    },
    {
      ...loanData,
      accounts: loanData?.accounts.slice(0, 1) || [],
    },
  ].filter(item => item.accounts.length > 0) as typeof accountData;

  return (
    <div>
      <PageHeader
        icon={<Icons.Users />}
        ignoreAccount
        title={isEditing ? 'Edit Authorized Viewer' : 'Create Authorized Viewer'}
        disableDownloadBtn
      />
      <div className={cn('px-[20px] grid gap-[20px]')}>
        <div className={cn('px-[20px] grid gap-[20px]')}>
          <ContentCard
            title={
              <div>
                <div className="font-bold text-xl text-[18px]">{isEditing ? 'Edit Viewer' : 'Create Viewer'}</div>
                <div className="text-[16px] leading-5 opacity-50 mt-2 max-w-[80%]">
                  {isEditing
                    ? 'Update the viewer information below. Click Save Changes when done.'
                    : 'Please fill in the following form and select at least one account to create an authorized viewer'}
                </div>
              </div>
            }
            actions={
              <div className="flex gap-2">
                <Button
                  className="text-destructive"
                  variant="link"
                  onClick={() => navigate('/guidance/authorized-viewers')}
                >
                  Cancel
                </Button>
                <Button variant="default" className="bg-addition7" onClick={handleSave}>
                  {isEditing ? 'Save Changes' : 'Create Viewer'}
                </Button>
              </div>
            }
            icon={
              <Button variant="ghost" className="p-0" onClick={() => navigate('/guidance/authorized-viewers')}>
                <MenuIcon icon={<Icons.CloseIcon />} variant="menu" size="large" />
              </Button>
            }
            titleStyle="items-center"
          />
        </div>

        <ContentCard
          title="Basic information"
          titleStyle="flex justify-center"
          icon={
            <MenuIcon
              icon={<Icons.InfoFill />}
              className="bg-destructive border-destructive"
              variant="white"
              size="large"
            />
          }
        >
          <ViewerForm
            key={viewer?.id || 'new'}
            initialData={
              viewer
                ? {
                    firstName: viewer.firstName || '',
                    lastName: viewer.lastName || '',
                    username: viewer.username || '',
                    email: viewer.email || '',
                    securityQuestion: viewer.securityQuestion || '',
                    securityAnswer: viewer.securityAnswer || '',
                  }
                : {}
            }
            onChange={setFormData}
            isEditing={isEditing}
          />
        </ContentCard>

        <ContentCard
          title="Accessible Account"
          actions={
            <div className="flex flex-col gap-2">
              <Input
                variant="search"
                placeholder="Search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-[200px]"
                icon={<Icons.Search />}
              />
              <Button variant="link" className="p-0 h-auto text-sm text-muted-foreground justify-start">
                Show Only Selected
              </Button>
            </div>
          }
          icon={
            <MenuIcon icon={<Icons.User />} className="bg-addition7 border-addition7" variant="white" size="large" />
          }
        >
          <Accounts
            data={filteredData}
            selectable
            selectedAccounts={selectedAccounts}
            onAccountSelect={handleAccountSelect}
          />
          <div className="flex justify-end mt-6">
            <FakePagination value="Page 1 of 1" disabled />
          </div>
        </ContentCard>
      </div>
    </div>
  );
}

export default CreateAuthorizedViewers;
